import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Controller, RegisterOptions, useForm } from 'react-hook-form';
import { isEqual } from 'lodash';
import { LinkIcon } from '@heroicons/react/24/outline';

import Modal, { ModalProps } from '@/components/ui/custom/modals/Modal';
import {
  ResourceFormProps,
  ResourceTypeEnum,
} from '@/models/_deprecated/Resource';
import Text from '@/components/ui/custom/dataDisplay/Text';
import RoundedButton from '@/components/ui/custom/buttons/RoundedButton';
import { ResourceFields } from '@/data/services/_deprecated/resourceServices';
import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import ResourceFileUpload from '@/components/resource/form/ResourceFileUpload';
import TextInput from '@/components/ui/custom/dataInput/TextInput';
import { validateLink } from '@/utils/validators/regex';

type ModalEditResourceProps = Omit<ModalProps, 'children'> & ResourceFormProps;

export default function ModalEditResource({
  visible,
  resourceType,
  onClose,
  onSubmit,
  loading,
  resource: defaultValues,
}: ModalEditResourceProps) {
  const [t] = useTranslation('translation', {
    keyPrefix: 'common',
  });

  const { t: tModalEditResource } = useTranslation('translation', {
    keyPrefix: 'modalEditResource',
  });

  const {
    watch,
    handleSubmit,
    register,
    reset,
    formState: { errors },
    control,
  } = useForm<ResourceFields>({ defaultValues });

  const resources: {
    [key in ResourceTypeEnum]: string | null;
  } = {
    FILE: t('file').toLowerCase(),
    LINK: 'link',
    NONE: null,
  };

  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    const subscribe = watch(value =>
      setHasChanges(!isEqual(value, defaultValues)),
    );
    return () => {
      subscribe.unsubscribe();
    };
  }, [defaultValues, watch]);

  const linkValidation: RegisterOptions<ResourceFields, 'resourceLink'> = {
    required: t('form.required', {
      field: 'link',
    }),
    pattern: {
      value: validateLink,
      message: t('form.invalid', { field: 'link' }),
    },
    validate: (value?: string | null) => {
      if (!value?.trim()) return t('form.empty', { field: 'link' });

      return true;
    },
  };

  const fileValidation = {
    required: t('form.required', {
      field: t('file').toLowerCase(),
    }),
  };

  const handleClose = () => {
    reset();
    onClose?.();
  };

  const block = !!Object.keys(errors).length;

  return (
    <Modal
      visible={visible}
      className={{ base: 'flex flex-col gap-6' }}
      onClose={handleClose}
    >
      <Text
        className="font-500 text-primary text-[1.25rem]"
        text={tModalEditResource('title', {
          resource: resources[resourceType],
        })}
      />
      <form className="flex flex-col gap-6" onSubmit={handleSubmit(onSubmit)}>
        <ConditionalRenderer condition={resourceType === ResourceTypeEnum.LINK}>
          <TextInput
            register={register(
              'resourceLink',
              resourceType === ResourceTypeEnum.LINK
                ? linkValidation
                : undefined,
            )}
            label="Link"
            labelPosition="left"
            className={{ label: 'w-fit' }}
            icon={<LinkIcon />}
            errorLabelText={errors.resourceLink?.message}
            color={errors.resourceLink?.message ? 'warning' : 'primary'}
          />
        </ConditionalRenderer>
        <ConditionalRenderer condition={resourceType === ResourceTypeEnum.FILE}>
          <Controller
            rules={
              resourceType === ResourceTypeEnum.FILE
                ? fileValidation
                : undefined
            }
            control={control}
            name="resourceFile"
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <ResourceFileUpload
                value={value}
                onChange={onChange}
                onRemove={() => onChange(null)}
                errorLabelText={error?.message}
              />
            )}
          />
        </ConditionalRenderer>
        <div className="flex w-full justify-around gap-6">
          <RoundedButton
            text={t('cancel')}
            color="neutral"
            className="w-full"
            onClick={handleClose}
            disabled={loading}
          />

          <RoundedButton
            text={t('confirm')}
            className="w-full"
            disabled={!hasChanges || block}
            loading={loading}
          />
        </div>
      </form>
    </Modal>
  );
}
